import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useMutation } from "convex/react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../Context/LanguageContext";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { api } from "../convexApi";
import BlogUnavailable from "../Components/BlogUnavailable";
import {
  isBlogBackendConfigured,
  normalizeBlogPayload,
  getStoredAdminKey,
  setStoredAdminKey,
} from "../services/blogService";
import "./blog.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const BlogCreateConnected = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const createPost = useMutation(api.blog.create);
  const [adminKey, setAdminKey] = useState(getStoredAdminKey());
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    tags: "React, Engineering",
    readTime: "5 min read",
    image: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("ValidImage"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, image: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const unlockAdmin = () => {
    const typedKey = window.prompt(t("EnterAdminKey"));
    if (!typedKey) return;
    const trimmedKey = typedKey.trim();
    setStoredAdminKey(trimmedKey);
    setAdminKey(trimmedKey);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!adminKey) {
      toast.error(t("AdminKeyRequired"));
      return;
    }

    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      toast.error(t("FillRequiredFields"));
      return;
    }

    try {
      const id = await createPost({
        adminKey,
        ...normalizeBlogPayload(form),
      });
      toast.success(t("PostCreated"));
      navigate(`/blog/${id}`);
    } catch (error) {
      toast.error(error.message || t("Error"));
    }
  };

  return (
    <motion.div
      className="blog-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      <div className="blog-page__inner">
        <div className="blog-detail__top">
          <Link to="/blog" className="blog-secondary-btn mono">
            <FiArrowLeft size={14} /> {t("Back")}
          </Link>
        </div>

        {!adminKey ? (
          <div className="blog-empty mono">
            <p>{t("AdminRequired")}</p>
            <button type="button" className="blog-secondary-btn" onClick={unlockAdmin}>
              {t("UnlockAdmin")}
            </button>
          </div>
        ) : (
          <section className="blog-form-wrap">
            <h1 className="blog-detail__title">{t("CreateBlogPost")}</h1>
            <form className="blog-form" onSubmit={handleSubmit}>
              <label className="blog-form__label mono">
                {t("title") || "title"}
                <input
                  name="title"
                  className="blog-form__input"
                  value={form.title}
                  onChange={handleChange}
                />
              </label>

              <label className="blog-form__label mono">
                {t("excerpt") || "excerpt"}
                <textarea
                  name="excerpt"
                  className="blog-form__textarea"
                  rows={3}
                  value={form.excerpt}
                  onChange={handleChange}
                />
              </label>

              <label className="blog-form__label mono">
                {t("content") || "content"}
                <textarea
                  name="content"
                  className="blog-form__textarea"
                  rows={10}
                  value={form.content}
                  onChange={handleChange}
                />
              </label>

              <p className="blog-form__hint mono">
                {t("Supported syntax:") || "Supported syntax:"}
                <br />• paragraphs
                <br />• **bold**
                <br />• \n
                <br />• `inline-code`
                <br />• fenced code blocks like {"```js"} ... {"```"}
              </p>

              <div className="blog-form__grid">
                <label className="blog-form__label mono">
                  {t("tags (comma separated)") || "tags (comma separated)"}
                  <input
                    name="tags"
                    className="blog-form__input"
                    value={form.tags}
                    onChange={handleChange}
                  />
                </label>

                <label className="blog-form__label mono">
                  {t("read time") || "read time"}
                  <input
                    name="readTime"
                    className="blog-form__input"
                    value={form.readTime}
                    onChange={handleChange}
                  />
                </label>
              </div>

              <div className="blog-form__grid">
                <label className="blog-form__label mono">
                  {t("image url") || "image url"}
                  <input
                    name="image"
                    className="blog-form__input"
                    value={form.image}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </label>

                <label className="blog-form__label mono">
                  {t("or upload image") || "or upload image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="blog-form__input"
                    onChange={handleFileUpload}
                  />
                </label>
              </div>

              {form.image ? (
                <img src={form.image} alt="Preview" className="blog-form__preview" />
              ) : null}

              <button type="submit" className="blog-primary-btn mono">
                <FiSave size={14} /> {t("SavePost")}
              </button>
            </form>
          </section>
        )}
      </div>
    </motion.div>
  );
};

const BlogCreate = () => {
  if (!isBlogBackendConfigured) {
    return <BlogUnavailable backTo="/blog" />;
  }

  return <BlogCreateConnected />;
};

export default BlogCreate;
